// src/services/EmployerService.ts - UPDATED to use /api/employers/me

import { employerApi } from '@/lib/api/employerApi';
import {
    EmployerWithDetails,
    UpdateEmployerRequest,
    UpdateUnitRequest,
    Employer,
    Unit,
    Organisation
} from '@/lib/models/employer';

export class EmployerService {

    // ==================== PROFILE OPERATIONS ====================

    /**
     * Get employer profile for the currently authenticated user
     * Uses JWT token to identify the user (no userId parameter needed)
     */
    static async getMyEmployerDetails(): Promise<EmployerWithDetails | null> {
        console.log('📥 [EmployerService] Getting current employer details');
        return await employerApi.getMyEmployerDetails();
    }

    /**
     * Get employer by ID (for admin operations)
     */
    static async getEmployerById(employerId: string): Promise<EmployerWithDetails> {
        console.log('📥 [EmployerService] Getting employer by ID:', employerId);
        return await employerApi.getEmployerById(employerId);
    }

    /**
     * Update employer profile
     */
    static async updateEmployer(
        employerId: string,
        data: UpdateEmployerRequest
    ): Promise<Employer> {
        console.log('💾 [EmployerService] Updating employer:', employerId);
        return await employerApi.updateEmployer(employerId, data);
    }

    /**
     * List all employers (admin only)
     */
    static async listEmployers(): Promise<Employer[]> {
        console.log('📋 [EmployerService] Listing all employers');
        return await employerApi.listEmployers();
    }

    // ==================== UNIT OPERATIONS ====================

    /**
     * Get unit by ID
     */
    static async getUnitById(unitId: string): Promise<Unit> {
        console.log('📥 [EmployerService] Getting unit by ID:', unitId);
        return await employerApi.getUnitById(unitId);
    }

    /**
     * Update unit information
     */
    static async updateUnit(unitId: string, data: UpdateUnitRequest): Promise<Unit> {
        console.log('💾 [EmployerService] Updating unit:', unitId);
        return await employerApi.updateUnit(unitId, data);
    }

    /**
     * Get units by organisation
     */
    static async getUnitsByOrganisation(organisationId: string): Promise<Unit[]> {
        console.log('📥 [EmployerService] Getting units for organisation:', organisationId);
        return await employerApi.getUnitsByOrganisation(organisationId);
    }

    /**
     * List all units
     */
    static async listUnits(): Promise<Unit[]> {
        console.log('📋 [EmployerService] Listing all units');
        return await employerApi.listUnits();
    }

    // ==================== ORGANISATION OPERATIONS ====================

    /**
     * Get organisation by ID (read-only for employers)
     */
    static async getOrganisationById(organisationId: string): Promise<Organisation> {
        console.log('📥 [EmployerService] Getting organisation by ID:', organisationId);
        return await employerApi.getOrganisationById(organisationId);
    }

    /**
     * List all organisations (read-only for employers)
     */
    static async listOrganisations(): Promise<Organisation[]> {
        console.log('📋 [EmployerService] Listing all organisations');
        return await employerApi.listOrganisations();
    }

    // ==================== HELPER FUNCTIONS ====================

    /**
     * Check if employer profile is complete
     */
    static isEmployerProfileComplete(details: EmployerWithDetails): boolean {
        return !!(
            details.employer.job_title &&
            details.unit.name &&
            details.unit.contact_email &&
            details.organisation.name
        );
    }

    /**
     * Calculate profile completion percentage
     */
    static getProfileCompletionPercentage(details: EmployerWithDetails): number {
        const fields = [
            details.employer.job_title,
            details.unit.name,
            details.unit.description,
            details.unit.contact_name,
            details.unit.contact_email,
            details.unit.contact_phone,
        ];

        const completed = fields.filter(Boolean).length;
        return Math.round((completed / fields.length) * 100);
    }

    /**
     * Get employer display name
     */
    static getEmployerDisplayName(details: EmployerWithDetails): string {
        return `${details.user.first_name} ${details.user.last_name}`;
    }

    /**
     * Get full context string (name, unit, organisation)
     */
    static getEmployerFullContext(details: EmployerWithDetails): string {
        return `${this.getEmployerDisplayName(details)} - ${details.unit.name}, ${details.organisation.name}`;
    }
}